import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { filter, take } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { ILoginModel } from 'src/app/store/login/login.models';
import { RegionModel } from 'libs/core/src/lib/models/tenants.model';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { isLoggedIn } from 'src/app/store/login/login.selectors';
import { Store } from '@ngrx/store';
import { loadLogin } from 'src/app/store/login/login.actions';
import { TranslateService } from '@ngx-translate/core';
import { CoreDarkModeService } from '../layout/services/dark-mode.service';

@Injectable({ providedIn: 'root' })
export class AuthMsalService {
  private _checkingAuth$ = new BehaviorSubject<boolean>(false);
  public checkingAuth$: Observable<boolean> = this._checkingAuth$.asObservable();
  public readonly protectedResourceScopes = [`https://${environment.B2C_tenantName}.onmicrosoft.com/b2c-api/b2c.read`];
  /**
   * Holds the Regions data fetched from the ProfilerService.
   * This is used to determine the current tenant and redirect accordingly.
   */
  Regions: RegionModel[];

  constructor(
    private authServiceB2C: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private cookieService: CookieService,
    private store: Store<Store>,
    @Optional() private darkModeService?: CoreDarkModeService,
    @Optional() private translateService?: TranslateService,
  ) {}

  initAuthFlow(): void {
    this._checkingAuth$.next(true);
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
      )
      .subscribe(() => {
        const activeAccount = this.authServiceB2C.instance.getActiveAccount();
        const allAccounts = this.authServiceB2C.instance.getAllAccounts();

        if (!activeAccount && allAccounts.length > 0) {
          this.authServiceB2C.instance.setActiveAccount(allAccounts[0]);
        }

        this.handleAuthentication();
      });
  }

  public initiatePublicLogin(isSupportUser: boolean): void {
    if (location.origin.includes('.arkik.com')) {
      location.assign('/login');
      return;
    }
    const activeAccount = this.authServiceB2C.instance.getActiveAccount();
    if (activeAccount && this.getAccountEmail(activeAccount)) {
      this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    const currentLang = this.getCurrentLanguage();
    const currentTheme = this.getCurrentTheme();
    const extraQueryParameters = {
      lang: currentLang,
      ui_locales: currentLang,
      ui_theme: currentTheme,
    };

    if (isSupportUser) {
      this.authServiceB2C.loginRedirect({
        authority: this.getSignInAuthority2(),
        scopes: this.protectedResourceScopes,
        extraQueryParameters,
      });
      return;
    }
    this.authServiceB2C.loginRedirect({
      scopes: this.protectedResourceScopes,
      extraQueryParameters,
    });
  }

  private handleAuthentication(): void {
    const url = location.origin;
    if (url.includes('.arkik.com')) {
      location.assign('/login');
      return;
    }

    let activeAccount = this.authServiceB2C.instance.getActiveAccount();
    const allAccounts = this.authServiceB2C.instance.getAllAccounts();

    if (!activeAccount && allAccounts.length === 0) {
      this.authServiceB2C.instance.loginRedirect();
      return;
    }

    if (!activeAccount && allAccounts.length > 0) {
      this.authServiceB2C.instance.setActiveAccount(allAccounts[0]);
      activeAccount = allAccounts[0];
    }

    if (!activeAccount) {
      this.restartAuthFlow();
      return;
    }

    const email = this.getAccountEmail(activeAccount);
    if (email) {
      this.validateSession(activeAccount);
      return;
    }

    this.restartAuthFlow();
  }

  private validateSession(account: AccountInfo): void {
    this.authServiceB2C
      .acquireTokenSilent({
        account,
        scopes: this.getScopes(),
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (!response.accessToken) {
            this.restartAuthFlow();
            return;
          }

          this._checkingAuth$.next(false);
          this.login();
          this.router.navigate(['/']);
        },
        error: () => {
          this.restartAuthFlow();
        },
      });
  }

  private restartAuthFlow(): void {
    this._checkingAuth$.next(false);
    this.clearSessionState();
    this.authServiceB2C.instance.setActiveAccount(null);
    this.authServiceB2C.instance.loginRedirect({ scopes: this.getScopes() });
  }

  logout(): void {
    this._checkingAuth$.next(false);
    this.clearSessionState();
    this.authServiceB2C.instance.setActiveAccount(null);
    this.authServiceB2C.logoutRedirect({
      postLogoutRedirectUri: environment.B2C_redirectUri,
    });
  }

  private getScopes(): string[] {
    return [`https://${environment.B2C_tenantName}.onmicrosoft.com/b2c-api/b2c.read`];
  }

  private getSignInAuthority2(): string {
    return `https://${environment.B2C_tenantName}.b2clogin.com/${environment.B2C_tenantName}.onmicrosoft.com/${environment.B2C_signInPolicy2}`;
  }

  private getAccountEmail(account: AccountInfo): string {
    const claims = account.idTokenClaims as { emails?: string[] | string; email?: string } | undefined;
    if (!claims) {
      return '';
    }

    if (Array.isArray(claims.emails)) {
      return claims.emails[0] ?? '';
    }

    return claims.emails ?? claims.email ?? '';
  }

  private extractMsalErrorCode(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const maybeError = error as { errorCode?: string; code?: string };
    return (maybeError.errorCode ?? maybeError.code ?? '').toLowerCase();
  }

  private clearSessionState(): void {
    this.cookieService.delete(environment.UserGuid);
    this.cookieService.delete(environment.BusinessPlanGuid);
    this.cookieService.delete(environment.ProductGuid);
    this.cookieService.delete(environment.CompanyGuid);
    this.clearMsalStorage();
  }

  private clearMsalStorage(): void {
    Object.keys(localStorage).forEach((key) => {
      if (this.matchesMsalStorageKey(key)) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (this.matchesMsalStorageKey(key)) {
        sessionStorage.removeItem(key);
      }
    });

    document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .filter((cookie) => this.matchesMsalCookieName(cookie.split('=')[0] ?? ''))
      .forEach((cookie) => {
        const cookieName = cookie.split('=')[0];
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
  }

  private matchesMsalStorageKey(key: string): boolean {
    const normalizedKey = key.toLowerCase();
    return normalizedKey.startsWith('msal.') || normalizedKey === 'msal';
  }

  private matchesMsalCookieName(cookieName: string): boolean {
    const normalizedName = cookieName.toLowerCase().trim();
    return normalizedName.startsWith('msal.') || normalizedName === 'msal' || normalizedName.startsWith('x-ms-cpim-');
  }

  private getCurrentLanguage(): 'en' | 'es' {
    const rawLang =
      this.translateService?.currentLang ||
      this.translateService?.defaultLang ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : null) ||
      (typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('language') || sessionStorage.getItem('lang')
        : null) ||
      (typeof navigator !== 'undefined' ? navigator.language : null);

    return rawLang && rawLang.toLowerCase().startsWith('es') ? 'es' : 'en';
  }

  private getCurrentTheme(): 'dark' | 'light' {
    if (this.darkModeService && typeof this.darkModeService.getCurrentTheme === 'function') {
      return this.darkModeService.getCurrentTheme();
    }
    const cwcTheme = typeof document !== 'undefined' ? document.documentElement?.getAttribute('cwc-theme') : null;
    if (cwcTheme === 'dark' || cwcTheme === 'light') {
      return cwcTheme;
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('isCwcDarkMode') === 'true' ? 'dark' : 'light';
    }
    return 'light';
  }

  private login(): void {
    const claims = this.authServiceB2C.instance.getActiveAccount()?.idTokenClaims;
    const email = claims?.emails ? (Array.isArray(claims.emails) ? claims.emails[0] : claims.emails) : claims?.email;
    if (email !== undefined) {
      const request: ILoginModel = { UserName: email as string, Password: '' };
      this.store.dispatch(loadLogin({ payload: request }));
      this.store
        .select(isLoggedIn)
        .pipe(
          take(1),
          filter((response) => Boolean(response)),
        )
        .subscribe({
          error: () => {
            this.cookieService.delete(environment.UserGuid);
            this.cookieService.delete(environment.BusinessPlanGuid);
            this.cookieService.delete(environment.ProductGuid);
            this.cookieService.delete(environment.CompanyGuid);
          },
        });
    }
  }
}
